import { useLingui } from '@lingui/react/macro'
import { LucideIcon } from 'components/LucideIcon'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'designSystem/components/Popover'
import { Separator } from 'designSystem/components/Separator'
import { useSidebar } from 'designSystem/components/Sidebar'
import { Text } from 'designSystem/components/Text'
import { useSheetPortalHost } from 'designSystem/hooks/useSheetPortalHost'
import type { WorkspaceSettingsNavigateHandler } from 'features/navbar/configSettings/ConfigSettingsSidebarMenuItems'
import React, { useCallback, useMemo, useState } from 'react'
import { GeckoSite } from 'types'

import { EmbeddedSelectorSection } from './EmbeddedSelectorSection'
import { EmbeddedSidebarBrandHomeLink } from './EmbeddedSidebarBrandHomeLink'
import { isSiteSelectorUseful } from './siteSelectorCapabilities'
import {
  EmbeddedSelectorChevron,
  EmbeddedSelectorIcon,
  EmbeddedSelectorLabels,
  EmbeddedSelectorTriggerButton,
} from './siteSelectorsSidebar.styles'
import { OrganizationWithSites, SiteSelectorsProps } from './siteSelectorsTypes'
import { useBrandHomeLink } from './useBrandHomeLink'
import { useEmbeddedSelectorActions } from './useEmbeddedSelectorActions'
import { useSiteSelectorsState } from './useSiteSelectorsState'

const EmbeddedSiteSelectorTrigger: React.FC<{
  organizationLabel: string
  siteLabel: string
  disabled: boolean
}> = ({ organizationLabel, siteLabel, disabled }) => {
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'

  return (
    <PopoverTrigger asChild>
      <EmbeddedSelectorTriggerButton
        type='button'
        $collapsed={isCollapsed}
        disabled={disabled}
        data-testid='org-dropdown'>
        <EmbeddedSelectorIcon>
          <LucideIcon name='Globe' size={18} />
        </EmbeddedSelectorIcon>
        <EmbeddedSelectorLabels $collapsed={isCollapsed}>
          <Text as='span' variant='caption' weight='semibold' truncate>
            {organizationLabel}
          </Text>
          <Text as='span' variant='caption' color='muted' truncate>
            {siteLabel}
          </Text>
        </EmbeddedSelectorLabels>
        <EmbeddedSelectorChevron $collapsed={isCollapsed}>
          <LucideIcon name='ChevronsUpDown' size={18} />
        </EmbeddedSelectorChevron>
      </EmbeddedSelectorTriggerButton>
    </PopoverTrigger>
  )
}

export const SiteSelectorsSidebar: React.FC<
  SiteSelectorsProps & {
    onWorkspaceNavigate: WorkspaceSettingsNavigateHandler
    showBrandHomeLink: boolean
  }
> = ({
  organizations,
  selectedOrganization,
  sites,
  selectedSite,
  onWorkspaceNavigate,
  showBrandHomeLink,
}) => {
  const { t } = useLingui()
  const { state, isMobile } = useSidebar()
  const isCollapsed = state === 'collapsed'
  const portalHost = useSheetPortalHost(isMobile)
  const {
    orgsWithAssociatedSites,
    sitesFilteredByOrganization,
    navigate,
    setDrawerEntry,
    setDrawerState,
    setOrgAndSiteIds,
    handleSiteSelect,
    handleEmbeddedOrgSelect,
    canEditOrganizations,
    canEditSites,
  } = useSiteSelectorsState({
    organizations,
    selectedOrganization,
    selectedSite,
    sites,
  })

  const [embeddedOpenList, setEmbeddedOpenList] = useState<
    'organization' | 'site' | null
  >(null)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [embeddedOrgSearch, setEmbeddedOrgSearch] = useState('')
  const [embeddedSiteSearch, setEmbeddedSiteSearch] = useState('')

  const resetEmbeddedSelectors = useCallback(() => {
    setEmbeddedOpenList(null)
    setEmbeddedOrgSearch('')
    setEmbeddedSiteSearch('')
  }, [])

  const {
    handleAddOrganization,
    handleAddSite,
    handleEditOrganization,
    handleEditSite,
  } = useEmbeddedSelectorActions({
    resetEmbeddedSelectors,
    setIsPopoverOpen,
    selectedOrganizationId: selectedOrganization?.id,
    navigate,
    setOrgAndSiteIds,
    setDrawerEntry,
    setDrawerState,
  })

  const filteredEmbeddedOrganizations = useMemo(() => {
    const searchTerm = embeddedOrgSearch.trim().toLowerCase()
    if (!searchTerm) return orgsWithAssociatedSites
    return orgsWithAssociatedSites.filter(({ organization }) =>
      organization.data.name.toLowerCase().includes(searchTerm)
    )
  }, [embeddedOrgSearch, orgsWithAssociatedSites])

  const filteredEmbeddedSites = useMemo(() => {
    const searchTerm = embeddedSiteSearch.trim().toLowerCase()
    if (!searchTerm) return sitesFilteredByOrganization
    return sitesFilteredByOrganization.filter((site) =>
      site.data.name.toLowerCase().includes(searchTerm)
    )
  }, [embeddedSiteSearch, sitesFilteredByOrganization])

  const organizationLabel = selectedOrganization?.data.name ?? t`Organization`
  const siteLabel = selectedSite?.data.name ?? t`Work Site`
  const brandHome = useBrandHomeLink({
    organizationId: selectedOrganization?.id,
    siteId: selectedSite?.id,
    enabled: showBrandHomeLink,
  })
  const canOpenSelector = isSiteSelectorUseful({
    organizationCount: orgsWithAssociatedSites.length,
    siteCountForSelectedOrganization: sitesFilteredByOrganization.length,
    capabilities: {
      canEditOrganizations,
      canEditSites,
    },
  })

  if (showBrandHomeLink && selectedOrganization && selectedSite) {
    return (
      <EmbeddedSidebarBrandHomeLink
        to={brandHome.to}
        destinationKey={brandHome.destinationKey}
        label={brandHome.label}
        organizationId={selectedOrganization.id}
        siteId={selectedSite.id}
        isCollapsed={isCollapsed}
        onWorkspaceNavigate={onWorkspaceNavigate}
      />
    )
  }

  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={(open) => {
        setIsPopoverOpen(open)
        if (!open) resetEmbeddedSelectors()
      }}>
      <EmbeddedSiteSelectorTrigger
        organizationLabel={organizationLabel}
        siteLabel={siteLabel}
        disabled={!canOpenSelector}
      />
      {portalHost.hostNode}
      <PopoverContent
        side='bottom'
        align='start'
        sideOffset={4}
        layout='flush'
        style={{ width: '15rem' }}
        fitViewport
        container={portalHost.container}>
        <EmbeddedSelectorSection<OrganizationWithSites>
          label={t`Organization`}
          selectedLabel={organizationLabel}
          selectedEditLabel={selectedOrganization?.data.name}
          onEditSelected={
            selectedOrganization
              ? (event) =>
                  void handleEditOrganization(event, selectedOrganization)
              : undefined
          }
          items={filteredEmbeddedOrganizations}
          getItemId={(item) => item.organization.id}
          getItemName={(item) => item.organization.data.name}
          isOpen={embeddedOpenList === 'organization'}
          onToggleOpen={() =>
            setEmbeddedOpenList((openList) =>
              openList === 'organization' ? null : 'organization'
            )
          }
          searchValue={embeddedOrgSearch}
          searchPlaceholder={t`Search organizations...`}
          onSearchChange={setEmbeddedOrgSearch}
          canEdit={canEditOrganizations}
          canAdd={canEditOrganizations}
          addLabel={t`Add Org`}
          onAdd={handleAddOrganization}
          onEdit={(event, item) =>
            void handleEditOrganization(event, item.organization)
          }
          onSelect={(item) => {
            setEmbeddedOpenList(null)
            setEmbeddedOrgSearch('')
            void handleEmbeddedOrgSelect(item)
          }}
        />
        <Separator />
        <EmbeddedSelectorSection<GeckoSite>
          label={t`Site`}
          selectedLabel={siteLabel}
          selectedEditLabel={selectedSite?.data.name}
          onEditSelected={
            selectedSite
              ? (event) => void handleEditSite(event, selectedSite)
              : undefined
          }
          items={filteredEmbeddedSites}
          getItemId={(site) => site.id}
          getItemName={(site) => site.data.name}
          isOpen={embeddedOpenList === 'site'}
          onToggleOpen={() =>
            setEmbeddedOpenList((openList) =>
              openList === 'site' ? null : 'site'
            )
          }
          searchValue={embeddedSiteSearch}
          searchPlaceholder={t`Search sites...`}
          onSearchChange={setEmbeddedSiteSearch}
          canEdit={canEditSites}
          canAdd={Boolean(canEditSites && selectedOrganization?.id)}
          addLabel={t`Add Site`}
          triggerDisabled={sitesFilteredByOrganization.length === 0}
          onAdd={handleAddSite}
          onEdit={(event, site) => void handleEditSite(event, site)}
          onSelect={(site) => {
            setEmbeddedOpenList(null)
            setEmbeddedSiteSearch('')
            void handleSiteSelect(site)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
